/**
 * ============================================
 * VALIDATION FUNCTIONS
 * ============================================
 */
import { 
  isEmailValid, 
  isValidName, 
  isValidBirthDate,
  isValidAddress,
  isValidCity,
  isValidZipCode
} from "./Validator";

export const checkCompletedSections = (formData, minimumBirthDate) => {
  return {
    coordonnees:
      formData?.coordonnees?.nom &&
      formData?.coordonnees?.prenom &&
      formData?.coordonnees?.email &&
      isValidName(formData?.coordonnees?.nom) &&
      isValidName(formData?.coordonnees?.prenom) &&
      isEmailValid(formData?.coordonnees?.email),
    genre: !!formData?.genre,
    dateNaissance:
      formData?.dateNaissance &&
      isValidBirthDate(formData?.dateNaissance) &&
      new Date(formData?.dateNaissance) <= minimumBirthDate,
    adresse:
      formData?.adresse?.rue &&
      formData?.adresse?.ville &&
      formData?.adresse?.codePostal &&
      isValidAddress(formData?.adresse?.rue) &&
      isValidCity(formData?.adresse?.ville) &&
      isValidZipCode(formData?.adresse?.codePostal),
    telephone: formData?.telephone && formData?.telephone.length >= 10,
    favoriteStore: !!formData?.favoriteStore,
  };
};

/**
 * ============================================
 * DATA TRANSFORMATION FUNCTIONS
 * ============================================
 */

export const transformFormDataToFlat = (formData) => {
  return {
    nom: formData?.coordonnees?.nom,
    prenom: formData?.coordonnees?.prenom,
    email: formData?.coordonnees?.email,
    gender: formData?.genre,
    birthDate: formData?.dateNaissance,
    address: formData?.adresse?.rue,
    city: formData?.adresse?.ville,
    zipCode: formData?.adresse?.codePostal,
    phoneNumber: formData?.phoneNumber || formData?.telephone,
    favoriteStore: formData?.favoriteStore,
  };
};

export const initializeFormDataFromUser = (user) => {
  return {
    coordonnees: {
      nom: user?.nom || '',
      prenom: user?.prenom || '',
      email: user?.email || '',
    },
    genre: user?.gender || '',
    dateNaissance: user?.birthDate
      ? new Date(user.birthDate).toISOString().split('T')[0]
      : '',
    adresse: {
      rue: user?.address || '',
      ville: user?.city || '',
      codePostal: user?.zipCode || '',
    },
    telephone: user?.phoneNumber || '',
    favoriteStore: user?.favoriteStore,
  };
};

/**
 * ============================================
 * HANDLER FACTORIES
 * ============================================
 */

export const createFieldChangeHandler = (setFormData) => {
  return (section, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [section]:
        typeof prev[section] === 'object' && prev[section] !== null && field
          ? { ...prev[section], [field]: value }
          : value,
    }));
  };
};

/**
 * ============================================
 * DATE UTILITIES
 * ============================================
 */

export const getMinimumBirthDate = () => {
  const date = new Date();
  return new Date(
    date.getFullYear() - 18,
    date.getMonth(),
    date.getDate(),
  );
};

export const getMaximumBirthDate = () => {
  const date = new Date();
  return new Date(
    date.getFullYear() - 99,
    date.getMonth(),
    date.getDate(),
  );
};

/**
 * ============================================
 * ADDRESS API UTILITIES
 * ============================================
 */

export const fetchAddressSuggestions = async (query, limit = 4) => {
  if (!query || query.length < 3) {
    return [];
  }

  try {
    const response = await fetch(
      `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(query)}&limit=${limit}`
    );
    const data = await response.json();
    
    return data.features || [];
  } catch (error) {
    console.error('Error fetching address suggestions:', error);
    return [];
  }
};

export const parseAddressFeature = (feature) => {
  const { properties } = feature;
  return {
    rue: properties.name || '',
    ville: properties.city || '',
    codePostal: properties.postcode || '',
    label: properties.label || '',
  };
};

/**
 * ============================================
 * NAVIGATION UTILITIES
 * ============================================
 */

export const createBackHandler = (navigate, previousRoute, ACCOUNT) => {
  return () => {
    if (previousRoute) {
      navigate(previousRoute);
    } else {
      navigate(ACCOUNT);
    }
  };
};

export const scrollToTop = (ref) => {
  if (ref.current) {
    ref.current.scrollIntoView({ behavior: 'smooth' });
  }
};

/**
 * ============================================
 * CONSTANTS
 * ============================================
 */

export const INITIAL_FORM_STATE = {
  coordonnees: {
    nom: '',
    prenom: '',
    email: '',
  },
  genre: '',
  dateNaissance: '',
  adresse: {
    rue: '',
    ville: '',
    codePostal: '',
  },
  telephone: '',
  favoriteStore: undefined,
};

export const INITIAL_COMPLETED_SECTIONS = {
  coordonnees: false,
  genre: false,
  dateNaissance: false,
  adresse: false,
  telephone: false,
  favoriteStore: false,
};

